import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FiSend, FiMessageCircle, FiCheck, FiCheckCircle, FiUsers, FiArrowLeft } from 'react-icons/fi';
import Layout from '../components/Layout';
import AudioPlayer from '../components/AudioPlayer';
import WhatsAppImage from '../components/WhatsAppImage';
import { whatsappAPI } from '../services/whatsappAPI';
import { clientesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// Função para formatar número de telefone
const formatPhoneNumber = (chatId) => {
  if (!chatId) return '';
  
  // Remover @s.whatsapp.net
  const number = chatId.replace('@s.whatsapp.net', '');
  
  // Formatar número brasileiro: +555599968677 -> 🇧🇷 +55 (55) 9996-8677
  if (number.startsWith('55') && number.length >= 12) {
    const countryCode = '55';
    const ddd = number.slice(2, 4);
    const phoneDigits = number.slice(4);
    
    if (phoneDigits.length === 8) {
      // Formatar como: 9996-8677
      const firstPart = phoneDigits.slice(0, 4);
      const secondPart = phoneDigits.slice(4);
      
      return `🇧🇷 +${countryCode} (${ddd}) ${firstPart}-${secondPart}`;
    }
  }
  
  // Fallback para outros formatos
  return `+${number}`;
};

const Container = styled.div`
  display: flex;
  height: calc(100vh - 80px);
  background: #f0f2f5;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: relative;

  @media (max-width: 768px) {
    height: calc(100vh - 60px);
    border-radius: 0;
  }
`;

const Sidebar = styled.div`
  width: 300px;
  background: #fff;
  border-right: 1px solid #e9edef;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease;

  @media (max-width: 768px) {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 10;
    transform: ${props => props.$show ? 'translateX(0)' : 'translateX(-100%)'};
  }
`;

const SidebarHeader = styled.div`
  padding: 16px;
  background: #f0f2f5;
  border-bottom: 1px solid #e9edef;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const SidebarTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  color: #111b21;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const SearchBox = styled.div`
  padding: 16px;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e9edef;
  border-radius: 20px;
  font-size: 14px;
  background: #f0f2f5;
  
  &:focus {
    outline: none;
    border-color: #00a884;
  }

  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 16px; /* Evita zoom no iOS */
  }
`;

const FilterBox = styled.div`
  padding: 0 16px 16px 16px;

  @media (max-width: 768px) {
    padding: 0 12px 12px 12px;
  }
`;

const FilterLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #667781;
  margin-bottom: 8px;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e9edef;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
  color: #111b21;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #00a884;
  }
`;

const ConversationsList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const ConversationItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f0f2f5;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 12px;
  
  &:hover {
    background: #f5f6f6;
  }
  
  ${props => props.$active && `
    background: #e7f3ff;
    border-left: 3px solid #00a884;
  `}

  @media (max-width: 768px) {
    padding: 10px 12px;
    gap: 10px;
  }
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.$color || '#00a884'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 18px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 16px;
  }
`;

const ConversationInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ConversationName = styled.div`
  font-weight: 500;
  color: #111b21;
  font-size: 16px;
  margin-bottom: 2px;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const LastMessage = styled.div`
  color: #667781;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const ChatArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;

  @media (max-width: 768px) {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: ${props => props.$show ? '20' : '0'};
    transform: ${props => props.$show ? 'translateX(0)' : 'translateX(100%)'};
    transition: transform 0.3s ease;
  }
`;

const ChatHeader = styled.div`
  padding: 16px;
  background: #f0f2f5;
  border-bottom: 1px solid #e9edef;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    padding: 12px;
    gap: 10px;
  }
`;

const ChatAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #00a884;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }
`;

const ChatInfo = styled.div`
  flex: 1;
`;

const ChatName = styled.div`
  font-weight: 500;
  color: #111b21;
  font-size: 16px;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const ChatStatus = styled.div`
  color: #667781;
  font-size: 13px;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="%23e5ddd5" opacity="0.3"/><circle cx="75" cy="75" r="1" fill="%23e5ddd5" opacity="0.3"/><circle cx="50" cy="10" r="0.5" fill="%23e5ddd5" opacity="0.2"/><circle cx="10" cy="60" r="0.5" fill="%23e5ddd5" opacity="0.2"/><circle cx="90" cy="40" r="0.5" fill="%23e5ddd5" opacity="0.2"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
  background-color: #efeae2;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    padding: 12px 8px;
  }
`;

const MessageBubble = styled.div`
  margin-bottom: 8px;
  display: flex;
  justify-content: ${props => props.$isOwn ? 'flex-end' : 'flex-start'};
`;

const MessageContent = styled.div`
  max-width: 70%;
  padding: 8px 12px;
  border-radius: 8px;
  background: ${props => props.$isOwn ? '#d9fdd3' : '#fff'};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;

  @media (max-width: 768px) {
    max-width: 85%;
    padding: 8px 10px;
  }
`;

const MessageText = styled.div`
  color: #111b21;
  font-size: 14px;
  line-height: 1.4;
  word-wrap: break-word;
  white-space: pre-wrap; /* Preservar quebras de linha */
  
  strong {
    font-weight: bold;
  }

  @media (max-width: 768px) {
    font-size: 15px;
    line-height: 1.5;
  }
`;

const MessageTime = styled.div`
  font-size: 11px;
  color: #667781;
  margin-top: 4px;
  text-align: ${props => props.$isOwn ? 'right' : 'left'};
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: ${props => props.$isOwn ? 'flex-end' : 'flex-start'};
`;

const MessageInput = styled.div`
  padding: 16px;
  background: #f0f2f5;
  border-top: 1px solid #e9edef;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    padding: 10px 12px;
    gap: 8px;
  }
`;

const InputField = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #e9edef;
  border-radius: 20px;
  font-size: 14px;
  background: #fff;
  
  &:focus {
    outline: none;
    border-color: #00a884;
  }

  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 16px; /* Evita zoom no iOS */
    border-radius: 24px;
  }
`;

const SendButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #00a884;
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
  flex-shrink: 0;
  
  &:hover {
    background: #008f73;
  }
  
  &:active {
    background: #008f73;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #667781;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;

  @media (max-width: 768px) {
    font-size: 48px;
    margin-bottom: 12px;
  }
`;

const EmptyText = styled.div`
  font-size: 18px;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const EmptySubtext = styled.div`
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 0 20px;
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #00a884;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const BackButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: #111b21;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background-color 0.2s;
  flex-shrink: 0;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  &:active {
    background: rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;


// Componente para renderizar texto formatado do WhatsApp
const FormattedMessage = ({ text }) => {
  // Função para processar o texto e aplicar formatação
  const formatText = (text) => {
    if (!text) return '';
    
    // Dividir por \n para criar quebras de linha
    const lines = text.split('\\n');
    
    return lines.map((line, lineIndex) => {
      // Processar negrito (*texto*) usando regex para encontrar todas as ocorrências
      const parts = [];
      let lastIndex = 0;
      const boldRegex = /\*([^\*]+?)\*/g;
      let match;
      
      // Processar todas as ocorrências de *texto* na linha
      while ((match = boldRegex.exec(line)) !== null) {
        // Adicionar texto antes do negrito (se houver)
        if (match.index > lastIndex) {
          parts.push(line.substring(lastIndex, match.index));
        }
        // Adicionar texto em negrito
        parts.push({ type: 'bold', text: match[1] });
        lastIndex = match.index + match[0].length;
      }
      
      // Adicionar texto restante
      if (lastIndex < line.length) {
        parts.push(line.substring(lastIndex));
      }
      
      // Se não encontrou nenhum negrito, usar o texto original
      if (parts.length === 0) {
        parts.push(line);
      }
      
      return (
        <React.Fragment key={lineIndex}>
          {parts.map((part, partIndex) => {
            if (typeof part === 'object' && part.type === 'bold') {
              return <strong key={partIndex}>{part.text}</strong>;
            }
            return <span key={partIndex}>{part}</span>;
          })}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  return <MessageText>{formatText(text)}</MessageText>;
};

const WhatsAppChat = () => {
  const location = useLocation();
  const { isAdmin } = useAuth();
  const [targetChatId, setTargetChatId] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('chatId');
  });
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const messagesContainerRef = useRef(null);
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const scrollPositionRef = useRef({ scrollTop: 0, scrollHeight: 0 });
  const isUserScrollingRef = useRef(false);
  
  // Estados para filtro de cliente (apenas para administradores)
  const [clientes, setClientes] = useState([]);
  const [selectedClienteId, setSelectedClienteId] = useState('');
  const [loadingClientes, setLoadingClientes] = useState(false);
  
  // Estado para controlar visibilidade em mobile
  const [showSidebar, setShowSidebar] = useState(true);
  
  // Hook para detectar tamanho da tela
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const adminUser = isAdmin();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const chatIdParam = params.get('chatId');
    setTargetChatId((prev) => (prev === chatIdParam ? prev : chatIdParam));
  }, [location.search]);
  
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // Em desktop, sempre mostrar sidebar e chat
      if (!mobile) {
        setShowSidebar(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Carregar lista de clientes se for administrador
  useEffect(() => {
    if (adminUser) {
      carregarClientes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminUser]);

  // Carregar conversas na inicialização e quando o filtro de cliente mudar
  useEffect(() => {
    if (adminUser) {
      if (loadingClientes) {
        return;
      }

      if (clientes.length === 0) {
        setConversations([]);
        setSelectedConversation(null);
        setMessages([]);
        setError('Nenhum cliente com credenciais de WhatsApp configuradas. Acesse Configurações e preencha evolution_api_url, evolution_api_key e evolution_instance.');
        return;
      }

      if (!selectedClienteId) {
        const primeiroCliente = clientes[0];
        if (primeiroCliente?.id) {
          setSelectedClienteId(String(primeiroCliente.id));
        }
        return;
      }
    }

    carregarConversas(targetChatId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClienteId, clientes, loadingClientes, adminUser, targetChatId]);

  const carregarClientes = async () => {
    try {
      setLoadingClientes(true);
      const response = await clientesAPI.listar({ limit: 1000, ativo: 'true' });
      if (response.data.success) {
        setClientes(response.data.data);
        if (adminUser && !selectedClienteId && response.data.data.length > 0) {
          const primeiroCliente = response.data.data[0];
          if (primeiroCliente?.id) {
            setSelectedClienteId(String(primeiroCliente.id));
          }
        }
      }
    } catch (err) {
      console.error('Erro ao carregar clientes:', err);
    } finally {
      setLoadingClientes(false);
    }
  };

  const carregarConversas = async (preferredChatId = targetChatId) => {
    try {
      const previousConversationId = selectedConversation?.id;
      setLoadingConversations(true);
      setError('');
      
      // Limpar conversas e mensagens antes de buscar novas
      setConversations([]);
      setSelectedConversation(null);
      setMessages([]);
      
      // Se for admin e tiver cliente selecionado, passar o cliente_id
      const params = {};
      if (adminUser && selectedClienteId) {
        params.cliente_id = selectedClienteId;
      }
      
      const response = await whatsappAPI.listarConversas(params);
      if (response.data.success) {
        const conversasRecebidas = response.data.data || [];
        setConversations(conversasRecebidas);

        let conversaSelecionada = null;
        const chatIdDesejado = preferredChatId || previousConversationId || null;

        if (chatIdDesejado) {
          conversaSelecionada = conversasRecebidas.find((conv) => conv.id === chatIdDesejado) || null;
        }

        if (!conversaSelecionada && !chatIdDesejado && conversasRecebidas.length > 0) {
          conversaSelecionada = conversasRecebidas[0];
        }

        if (conversaSelecionada) {
          setSelectedConversation(conversaSelecionada);
          setMessages([]);
          setCurrentPage(1);
          setHasMoreMessages(true);
          setIsScrollingUp(false);
          setShouldAutoScroll(true);
          isUserScrollingRef.current = false;
          setError('');

          if (isMobile && preferredChatId && conversaSelecionada.id === preferredChatId) {
            setShowSidebar(false);
          }

          carregarMensagens(conversaSelecionada.id);
        } else {
          setSelectedConversation(null);
          setMessages([]);
          if (preferredChatId) {
            setError('Não foi possível localizar a conversa do contato selecionado.');
          }
          // Em mobile, se não houver conversas, mostrar sidebar
          if (isMobile) {
            setShowSidebar(true);
          }
        }
      } else {
        // Se não tiver sucesso, limpar tudo
        setConversations([]);
        setSelectedConversation(null);
        setMessages([]);
        setError(response.data.error || 'Erro ao carregar conversas');
        // Em mobile, mostrar sidebar se não houver conversas
        if (isMobile) {
          setShowSidebar(true);
        }
      }
    } catch (err) {
      console.error('Erro ao carregar conversas:', err);
      // Limpar tudo em caso de erro
      setConversations([]);
      setSelectedConversation(null);
      setMessages([]);
      
      // Mostrar mensagem de erro mais específica
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          'Erro ao carregar conversas. Verifique se a Evolution API está rodando.';
      setError(errorMessage);
    } finally {
      setLoadingConversations(false);
    }
  };

  const carregarMensagens = async (chatId, page = 1, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoadingMessages(true);
        setCurrentPage(1);
        setHasMoreMessages(true);
      }
      
      // Se for admin e tiver cliente selecionado, passar o cliente_id
      const params = { 
        limit: 50, 
        page: page 
      };
      if (adminUser && selectedClienteId) {
        params.cliente_id = selectedClienteId;
      }
      
      const response = await whatsappAPI.buscarMensagens(chatId, params);
      
      if (response.data.success) {
        const newMessages = response.data.data;
        
        if (append) {
          // Adicionar mensagens mais antigas no início, evitando duplicatas
          setMessages(prev => {
            const existingIds = new Set(prev.map(msg => msg.id));
            const uniqueNewMessages = newMessages.filter(msg => !existingIds.has(msg.id));
            return [...uniqueNewMessages, ...prev];
          });
        } else {
          // Primeira carga ou mudança de conversa
          setMessages(newMessages);
        }
        
        // Verificar se há mais mensagens para carregar
        setHasMoreMessages(newMessages.length === 50);
        setCurrentPage(page);
        
        return Promise.resolve();
      }
    } catch (err) {
      console.error('Erro ao carregar mensagens:', err);
      setError('Erro ao carregar mensagens.');
      return Promise.reject(err);
    } finally {
      setLoadingMessages(false);
      setLoadingMore(false);
    }
  };

  const carregarMaisMensagens = async () => {
    if (!hasMoreMessages || loadingMore || !selectedConversation) return;
    
    const container = messagesContainerRef.current;
    if (!container) return;
    
    // Salvar posição atual ANTES de carregar novas mensagens
    const currentScrollHeight = container.scrollHeight;
    const currentScrollTop = container.scrollTop;
    
    // Marcar que estamos carregando mensagens antigas
    setIsScrollingUp(true);
    setShouldAutoScroll(false);
    isUserScrollingRef.current = true;
    
    // Salvar posição de referência
    scrollPositionRef.current = { scrollTop: currentScrollTop, scrollHeight: currentScrollHeight };
    
    const nextPage = currentPage + 1;
    
    try {
      await carregarMensagens(selectedConversation.id, nextPage, true);
      
      // Restaurar posição do scroll após carregar as mensagens antigas
      setTimeout(() => {
        if (container) {
          const newScrollHeight = container.scrollHeight;
          const heightDifference = newScrollHeight - currentScrollHeight;
          const newScrollTop = currentScrollTop + heightDifference;
          
          container.scrollTop = newScrollTop;
          
          // Manter estado para evitar scroll automático
          isUserScrollingRef.current = true;
        }
      }, 10);
    } catch (error) {
      // Em caso de erro, resetar os estados
      setIsScrollingUp(false);
      setShouldAutoScroll(true);
      isUserScrollingRef.current = false;
    }
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setMessages([]); // Limpar mensagens anteriores
    setCurrentPage(1);
    setHasMoreMessages(true);
    setIsScrollingUp(false);
    setShouldAutoScroll(true); // Habilitar auto-scroll para nova conversa
    isUserScrollingRef.current = false; // Resetar flag de scroll do usuário
    setError('');
    
    // Em mobile, esconder sidebar e mostrar chat
    if (isMobile) {
      setShowSidebar(false);
    }
    
    // Carregar mensagens - o scroll será feito pelo useEffect
    carregarMensagens(conversation.id);
  };
  
  const handleBackToList = () => {
    setShowSidebar(true);
  };

  useEffect(() => {
    // Scroll automático quando:
    // - Houver mensagens
    // - Não estiver carregando
    // - Auto-scroll estiver habilitado
    // - Usuário não estiver fazendo scroll manual
    if (!isScrollingUp && !loadingMore && !loadingMessages && messages.length > 0 && shouldAutoScroll && !isUserScrollingRef.current) {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          const container = messagesContainerRef.current;
          container.scrollTop = container.scrollHeight;
        }
      }, 100);
    }
  }, [messages, isScrollingUp, loadingMore, loadingMessages, shouldAutoScroll]);

  // Detectar scroll para carregar mais mensagens
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      
      // Detectar se está próximo do topo (100px de margem) para carregar mensagens antigas
      if (scrollTop < 100 && hasMoreMessages && !loadingMore && !loadingMessages) {
        carregarMaisMensagens();
      }
      
      // Marcar que o usuário está fazendo scroll manual
      // Não reabilitar auto-scroll automaticamente
      isUserScrollingRef.current = true;
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMoreMessages, loadingMore, loadingMessages, selectedConversation]);

  // Preservar posição do scroll quando mensagens antigas são adicionadas
  useEffect(() => {
    if (isScrollingUp && messagesContainerRef.current && isUserScrollingRef.current) {
      const container = messagesContainerRef.current;
      const { scrollTop, scrollHeight } = scrollPositionRef.current;
      
      // Aguardar para o DOM ser atualizado
      setTimeout(() => {
        if (container) {
          const newScrollHeight = container.scrollHeight;
          const heightDifference = newScrollHeight - scrollHeight;
          const newScrollTop = scrollTop + heightDifference;
          
          container.scrollTop = newScrollTop;
        }
      }, 10);
    }
  }, [messages, isScrollingUp]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending || !selectedConversation) return;

    setSending(true);
    setError('');
    setShouldAutoScroll(true); // Habilitar auto-scroll ao enviar mensagem
    isUserScrollingRef.current = false; // Resetar flag de scroll do usuário
    setIsScrollingUp(false); // Resetar estado de scroll para cima
    
    // Adicionar mensagem temporária
    const tempMessage = {
      id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      text: newMessage,
      timestamp: new Date().toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      }),
      isOwn: true,
      status: 'sending'
    };

    setMessages(prev => [...prev, tempMessage]);
    const messageText = newMessage;
    setNewMessage('');

    try {
      const response = await whatsappAPI.enviarMensagem({
        chatId: selectedConversation.id,
        message: messageText
      });

      if (response.data.success) {
        // Atualizar mensagem com status enviado
        setMessages(prev => 
          prev.map(msg => 
            msg.id === tempMessage.id 
              ? { ...msg, status: 'sent', id: response.data.data.id }
              : msg
          )
        );
      } else {
        throw new Error(response.data.error || 'Erro ao enviar mensagem');
      }
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
      setError('Erro ao enviar mensagem. Verifique se a Evolution API está conectada.');
      
      // Remover mensagem temporária em caso de erro
      setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sending':
        return <LoadingSpinner />;
      case 'delivered':
        return <FiCheck />;
      case 'read':
        return <FiCheckCircle />;
      default:
        return null;
    }
  };

  return (
    <Layout title="WhatsApp - Chat com IA">
      <Container>
        <Sidebar $show={showSidebar || !isMobile}>
          <SidebarHeader>
            <SidebarTitle>
              <FiMessageCircle />
              Conversas
            </SidebarTitle>
          </SidebarHeader>
          
          <SearchBox>
            <SearchInput
              type="text"
              placeholder="Pesquisar conversas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBox>
          
          {isAdmin() && (
            <FilterBox>
              <FilterLabel>
                <FiUsers style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                Filtrar por Cliente
              </FilterLabel>
              <Select
                value={selectedClienteId}
                onChange={(e) => setSelectedClienteId(e.target.value)}
                disabled={loadingClientes}
              >
                <option value="">Todos os clientes</option>
                {clientes.map((cliente) => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.nome_fantasia || cliente.nome_social || `Cliente ${cliente.id}`}
                  </option>
                ))}
              </Select>
            </FilterBox>
          )}
          
          <ConversationsList>
            {loadingConversations ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
                color: '#667781'
              }}>
                <LoadingSpinner />
                <span style={{ marginLeft: '8px' }}>Carregando conversas...</span>
              </div>
            ) : (
              filteredConversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                $active={selectedConversation?.id === conversation.id}
                onClick={() => handleSelectConversation(conversation)}
              >
                <Avatar $color="#00a884">
                  {conversation.avatar}
                </Avatar>
                <ConversationInfo>
                  <ConversationName>{conversation.name}</ConversationName>
                  <LastMessage>{conversation.lastMessage}</LastMessage>
                  <MessageTime>{conversation.timestamp}</MessageTime>
                </ConversationInfo>
                {conversation.unread > 0 && (
                  <div style={{
                    background: '#00a884',
                    color: 'white',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    {conversation.unread}
                  </div>
                )}
              </ConversationItem>
              ))
            )}
          </ConversationsList>
        </Sidebar>

        <ChatArea $show={(selectedConversation && (!showSidebar || !isMobile))}>
          {selectedConversation ? (
            <>
              <ChatHeader>
                {isMobile && (
                  <BackButton onClick={handleBackToList} aria-label="Voltar para lista de conversas">
                    <FiArrowLeft size={24} />
                  </BackButton>
                )}
                <ChatAvatar>
                  {selectedConversation.avatar}
                </ChatAvatar>
                <ChatInfo>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <ChatName>{selectedConversation.name}</ChatName>
                    <ChatStatus style={{ fontSize: '13px', color: '#667781', fontWeight: 'normal' }}>
                      {formatPhoneNumber(selectedConversation.id)}
                    </ChatStatus>
                  </div>
                </ChatInfo>
              </ChatHeader>

              <MessagesContainer ref={messagesContainerRef}>
                {error && (
                  <div style={{
                    background: '#ff6b6b',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    margin: '8px 0',
                    textAlign: 'center'
                  }}>
                    {error}
                  </div>
                )}
                
                {/* Indicador de carregamento de mensagens */}
                {loadingMessages && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '200px',
                    color: '#667781',
                    fontSize: '14px'
                  }}>
                    <LoadingSpinner style={{ marginRight: '8px' }} />
                    Carregando mensagens...
                  </div>
                )}
                
                {/* Indicador de carregamento de mensagens antigas */}
                {loadingMore && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '16px',
                    color: '#667781',
                    fontSize: '14px'
                  }}>
                    <LoadingSpinner style={{ marginRight: '8px' }} />
                    Carregando mensagens antigas...
                  </div>
                )}
                
                {messages.map((message) => (
                  <MessageBubble key={message.id} $isOwn={message.isOwn}>
                    <MessageContent $isOwn={message.isOwn}>
                      {message.messageType === 'image' && message.imageData ? (
                        <WhatsAppImage 
                          imageData={message.imageData} 
                          isOwn={message.isOwn}
                        />
                      ) : message.messageType === 'audio' && message.audioData ? (
                        <AudioPlayer 
                          audioData={message.audioData} 
                          isOwn={message.isOwn}
                        />
                      ) : (
                        <FormattedMessage text={message.text} />
                      )}
                      <MessageTime $isOwn={message.isOwn}>
                        {message.timestamp}
                        {message.isOwn && getStatusIcon(message.status)}
                      </MessageTime>
                    </MessageContent>
                  </MessageBubble>
                ))}
              </MessagesContainer>

              <MessageInput>
                <InputField
                  type="text"
                  placeholder="Digite uma mensagem..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={true}
                  style={{
                    cursor: 'not-allowed',
                    opacity: 0.6
                  }}
                />
                <SendButton
                  onClick={handleSendMessage}
                  disabled={true}
                >
                  <FiSend />
                </SendButton>
              </MessageInput>
            </>
          ) : (
            <EmptyState>
              <EmptyIcon>
                <FiMessageCircle />
              </EmptyIcon>
              <EmptyText>Selecione uma conversa</EmptyText>
              <EmptySubtext>Escolha uma conversa para começar a conversar</EmptySubtext>
            </EmptyState>
          )}
        </ChatArea>
      </Container>
    </Layout>
  );
};

export default WhatsAppChat;

